// FAQ + prep guide content.

export const FAQS = [
    {
        q: "Are Dr. Kichian's consultations and procedures covered by OHIP?",
        a: "Yes. Consultations, colonoscopies, gastroscopies, sigmoidoscopies, and follow-up visits with Dr. Kichian are fully covered by the Ontario Health Insurance Plan (OHIP) when you have a valid Ontario Health card and a referral from your family physician. All procedures are performed at Queensway Carleton Hospital in Ottawa. A few small items are not covered by OHIP — most commonly the bowel prep kit (Pico-Salax or Peglite), which you buy over the counter at any pharmacy. If you don't have an OHIP card or you're visiting from another province, please call our office before booking so we can walk you through the options.",
    },
    {
        q: "What are the typical wait times to see Dr. Kichian?",
        a: "Wait times depend on how urgent your case is, and every referral is triaged individually by Dr. Kichian's team. In general, patients in Ottawa and the surrounding area can expect: routine consultations within 4–8 weeks; urgent consultations within 1 week; and emergent concerns (severe pain, heavy bleeding, chest pain) should go directly to the emergency department at Queensway Carleton Hospital or call 911. Once you've been seen for consultation, procedure scheduling (colonoscopy or gastroscopy) typically follows within a few weeks. To keep wait times as short as possible, please ask your family physician to include recent test results, symptoms, and urgency on the fax referral so our team can prioritize correctly.",
    },
    {
        q: "How does the referral process to Dr. Kichian work?",
        a: "The referral process is straightforward and physician-to-physician. Step 1: Speak with your family physician about your digestive concern. Step 2: Your family physician faxes a completed referral to our office at (613) 728-0091 on clinic letterhead, including the indication, relevant history, current medications, and any recent test results. Please make sure your current email address and cellphone number are included — we send appointment confirmations by email (via the Ocean Platform) and the virtual-visit link by SMS. Step 3: Our team reviews and triages the referral. Step 4: We contact you directly to schedule a virtual or in-person consultation at Queensway Carleton Hospital. Step 5: After your visit, a written summary and next-step recommendations are faxed back to your referring physician. Self-referrals from patients are not accepted — please always start with your family doctor.",
    },
    {
        q: "When should I have a colonoscopy?",
        a: "For average-risk adults, screening colonoscopy is recommended starting at age 45. If you have a family history of colon cancer or polyps, or symptoms like rectal bleeding or unexplained anemia, screening or diagnostic colonoscopy may be recommended earlier.",
    },
    {
        q: "How long does a procedure take?",
        a: "Colonoscopy typically takes 20–40 minutes, and gastroscopy (upper endoscopy) usually takes 10–20 minutes. With prep, recovery, and check-in, plan on a few hours for the whole visit.",
    },
    {
        q: "Is sedation available?",
        a: "Yes. We use gentle sedation for both colonoscopy and gastroscopy so that most patients don't remember the procedure and are comfortable throughout. You'll need a responsible adult to drive you home.",
    },
    {
        q: "How should I prepare?",
        a: "You'll receive detailed, personalized instructions before your procedure. For colonoscopy, this includes a clear-liquid diet the day before and a split-dose bowel prep. For gastroscopy, you'll fast from midnight the night before. Our team walks you through every step.",
    },
    {
        q: "What happens after my appointment?",
        a: "You'll receive written findings and next-step recommendations. If biopsies were taken, results are typically available within 5–10 days and reviewed with you at follow-up.",
    },
    {
        q: "What digestive symptoms should not be ignored?",
        a: "Please seek evaluation for: blood in the stool or black tarry stools, difficulty swallowing, unintentional weight loss, persistent vomiting, iron-deficiency anemia without cause, and any new or worsening abdominal pain — especially with fever.",
    },
    {
        q: "Do I need an OHIP card or referral?",
        a: "Yes — please bring your valid Ontario Health (OHIP) card to every visit. Consultations with Dr. Kichian require a referral from your family physician; self-referrals from patients are not accepted.",
    },
    {
        q: "Is my visit confidential?",
        a: "Absolutely. All communications, records, and interactions are covered by strict Canadian privacy standards (PIPEDA and Ontario's PHIPA). Your information is shared only with your consent and with providers involved in your care.",
    },
];

export const PREP_GUIDES = [
    {
        slug: "preparing-for-your-visit",
        title: "Preparing for Your Visit",
        icon: "Clipboard",
        summary:
            "A little preparation makes your visit far more productive. Here's what to bring and how to make the most of your time.",
        sections: [
            {
                heading: "Bring these documents",
                items: [
                    "Photo ID and Ontario Health (OHIP) card",
                    "List of current medications, doses, and supplements",
                    "Prior GI test reports (endoscopy, imaging, biopsies) if available",
                    "Any records from other specialists related to your concern",
                ],
            },
            {
                heading: "Come prepared to share",
                items: [
                    "When your symptoms began and how they've changed",
                    "What makes symptoms better or worse",
                    "How they impact your daily life",
                    "What you're hoping to accomplish with this visit",
                ],
            },
            {
                heading: "Questions to consider asking",
                items: [
                    "What do you think is causing my symptoms?",
                    "What tests — if any — are recommended and why?",
                    "What are the treatment options and how do we choose?",
                    "What can I do at home starting today?",
                ],
            },
        ],
        callout: {
            tone: "info",
            text: "Please arrive 15 minutes early to complete any remaining paperwork.",
        },
    },
    {
        slug: "preparing-for-colonoscopy",
        title: "Preparing for Colonoscopy",
        icon: "ClipboardList",
        summary:
            "A clean colon is essential for a safe, accurate exam. Here's how to prepare comfortably in the days leading up to your procedure.",
        sections: [
            {
                heading: "5–7 days before",
                items: [
                    "Review medications with our team — especially blood thinners and diabetes medications",
                    "Arrange a responsible adult to drive you home after the procedure",
                    "Purchase your prescribed prep and any recommended supplies (clear broth, sports drinks, hydration)",
                ],
            },
            {
                heading: "1–2 days before",
                items: [
                    "Follow a low-residue diet (avoid nuts, seeds, whole grains, raw vegetables)",
                    "Stop supplements that may interfere (iron, fiber supplements)",
                ],
            },
            {
                heading: "The day before",
                items: [
                    "Clear-liquid diet only (no red or purple liquids)",
                    "Begin split-dose prep as prescribed — the second dose is typically about 6 hours before your procedure",
                    "Continue drinking clear fluids until about 6 hours before your procedure to stay hydrated",
                ],
            },
            {
                heading: "Procedure day",
                items: [
                    "Nothing by mouth for the last 6 hours (check specific instructions)",
                    "Wear comfortable clothing",
                    "Leave valuables at home; bring only ID and your OHIP card",
                ],
            },
        ],
        callout: {
            tone: "warning",
            text: "Completing your prep as prescribed is the single biggest factor in a successful colonoscopy. If in doubt — call us.",
        },
    },
    {
        slug: "preparing-for-gastroscopy",
        title: "Preparing for Gastroscopy (Upper Endoscopy)",
        icon: "ClipboardCheck",
        summary:
            "Preparation for gastroscopy is simpler than colonoscopy. Here's what to expect on the day before and morning of your procedure.",
        sections: [
            {
                heading: "1–2 days before",
                items: [
                    "Continue normal diet unless otherwise instructed",
                    "Review medications with our team (especially blood thinners, diabetes medications, and iron)",
                    "Arrange a responsible adult to drive you home after the procedure",
                ],
            },
            {
                heading: "The day before",
                items: [
                    "Eat a light dinner",
                    "Nothing by mouth after midnight — no food, water, gum, mints, or coffee",
                ],
            },
            {
                heading: "Procedure day",
                items: [
                    "Nothing by mouth for 6 hours prior to your procedure",
                    "Take essential medications with a small sip of water if instructed",
                    "Wear comfortable clothing",
                    "Leave valuables at home; plan to spend 1–2 hours at the facility total",
                ],
            },
        ],
        callout: {
            tone: "info",
            text: "Please contact us if you have questions about your medications or if anything changes in your health before the procedure.",
        },
    },
    {
        slug: "medication-guidelines",
        title: "Medication Guidelines",
        icon: "Pill",
        summary:
            "Some medications need adjustment before endoscopic procedures. Never stop a medication without checking with us first.",
        sections: [
            {
                heading: "Blood thinners",
                items: [
                    "Warfarin, apixaban, rivaroxaban, dabigatran: usually adjusted 3–5 days before, coordinated with your prescribing physician",
                    "Aspirin: usually continued unless we advise otherwise",
                    "Clopidogrel and similar: individualized based on indication",
                ],
            },
            {
                heading: "Diabetes medications",
                items: [
                    "Adjust or hold insulin and oral hypoglycemics on prep and procedure days",
                    "Bring your glucometer and treat lows as usual",
                    "We'll provide a written plan",
                ],
            },
            {
                heading: "Other considerations",
                items: [
                    "Iron supplements: hold 5 days before colonoscopy",
                    "GLP-1 agonists (semaglutide, tirzepatide): may require additional fasting; discuss with our team",
                    "Continue essential medications (blood pressure, seizure) with a small sip of water",
                ],
            },
        ],
        callout: {
            tone: "warning",
            text: "Please share every medication and supplement you take, including over-the-counter and herbal products.",
        },
    },
    {
        slug: "procedure-day-expectations",
        title: "Procedure Day: What to Expect",
        icon: "CalendarCheck",
        summary:
            "Knowing what to expect makes procedure day calmer and smoother. Here's the flow, start to finish.",
        sections: [
            {
                heading: "Arrival & check-in",
                items: [
                    "Arrive 30 minutes early",
                    "Bring ID, OHIP card, and your driver",
                    "Change into a gown; jewelry and valuables stay with your driver",
                ],
            },
            {
                heading: "Before the procedure",
                items: [
                    "Nurse review of medications, allergies, and prep",
                    "IV placement for sedation",
                    "Brief meeting with your gastroenterologist and anesthesia team",
                ],
            },
            {
                heading: "The procedure",
                items: [
                    "Gentle sedation is administered",
                    "You lie on your side; the exam is completed by trained specialists",
                    "Typically 20–40 minutes (colonoscopy) or 10–20 minutes (gastroscopy)",
                ],
            },
            {
                heading: "Recovery & discharge",
                items: [
                    "You'll wake up in the recovery area",
                    "A written report is reviewed with you and your driver",
                    "Plan to rest at home for the remainder of the day",
                ],
            },
        ],
        callout: {
            tone: "info",
            text: "You must have a responsible adult with you to drive you home. Rideshare services are not accepted for post-sedation discharge.",
        },
    },
    {
        slug: "recovery-information",
        title: "Recovery Information",
        icon: "Bandage",
        summary:
            "Most patients recover quickly. Here's what to expect in the hours and days after your procedure.",
        sections: [
            {
                heading: "First 24 hours",
                items: [
                    "No driving, alcohol, or important decisions until the next morning",
                    "Start with light foods; return to a regular diet as tolerated",
                    "Some bloating and cramping after colonoscopy is normal; passing gas provides relief",
                ],
            },
            {
                heading: "Return to activities",
                items: [
                    "Most patients return to work the next day",
                    "Avoid heavy lifting for 24 hours after polyp removal",
                    "Resume medications per your written instructions",
                ],
            },
            {
                heading: "Call us if you experience",
                items: [
                    "Severe abdominal pain not relieved by passing gas",
                    "Fever above 38°C (100.4°F)",
                    "Significant rectal bleeding or vomiting blood",
                    "Chest pain or difficulty breathing (call 911 immediately)",
                ],
            },
        ],
        callout: {
            tone: "warning",
            text: "Your safety matters. If in doubt, call our office — we'd rather answer a question than miss a warning sign.",
        },
    },
];

export function getPrepGuideBySlug(slug) {
    return PREP_GUIDES.find((g) => g.slug === slug);
}
